"use client";
import PerfectMatch from "@/components/tools/checkEligibility/PerfectMatches";
import Back from "@/components/common/Back";
import { useAppSelector } from "@/store/hooks";
import Image from "next/image";
import Link from "next/link";
import { checkCountry, replacedName } from "@/utils/Constants";
import { formatCountryUrl } from "@/utils/formatCountryUrl";
import { getFormattedCountryName } from "@/utils/getFormattedCountryName";
import { Skeleton } from "@/components/ui/skeleton";
import { NewNavBar } from "@/components/common/NewNavBar";

const UniversityCountrySkeletonCard = () => (
  <div className="bg-white rounded-xl pb-4 shadow-md relative rounded-t-2xl overflow-hidden">
    <Skeleton className="w-full h-[140px] sm:h-[200px] md:h-[230px] xl:h-[260px]" />
    <div className="px-3 relative z-10 mt-3 space-y-2">
      <Skeleton className="h-5 w-3/4 mx-auto" />
      <Skeleton className="h-5 w-1/2 mx-auto" />
      <Skeleton className="h-4 w-2/3 mx-auto" />
    </div>
  </div>
);

const AllUniversitiesCards = () => {
  const { countryList, loading } = useAppSelector((state) => state.country);
  const countryImage = (name: string) => {
    return `/assets/images/png/${name.replace(/\s+/g, "")}_dashboard.png`;
  };

  return (
    <div className="bg-[#f4edff]">
      <NewNavBar />
      <div className="container 2xl:max-w-[1628px] min-h-screen relative overflow-hidden pt-24 pb-10 sm:pb-20">
        <Back />
        <Image
          className="absolute top-1/2 left-1/2 max-w-[1356px] w-full -translate-y-1/2 -translate-x-1/2"
          src="/assets/images/png/globalMap.png"
          alt="globe-map"
          width={1356}
          height={727}
        />
        <div className="mt-7 sm:mt-8 md:mt-9 lg:mt-10 xl:mt-12 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-[17px] sm:gap-5 md:gap-7 lg:gap-11 3xl:gap-x-[46px] gap-y-4 sm:gap-y-5 md:gap-y-6 lg:gap-y-9 3xl:gap-y-[48px] relative z-10">
          {loading
            ? Array.from({ length: 8 }).map((_, index) => (
                <UniversityCountrySkeletonCard key={index} />
              ))
            : countryList?.map((item) => {
                const image = countryImage(
                  item.country_name.replace("europe -", ""),
                );
                const formattedSlug = formatCountryUrl(item.country_name);

                return (
                  <Link
                    href={`/all-universities/${encodeURIComponent(formattedSlug)}`}
                    key={item.country_id}
                    className="bg-white rounded-xl pb-4 shadow-md relative rounded-t-2xl overflow-hidden"
                  >
                    <span className="inline-block relative">
                      <Image
                        className="max-h-[200px] sm:max-h-[300px] object-cover"
                        src={image || ""}
                        alt="country-image"
                        width={382}
                        height={382}
                      />
                      <Image
                        className="w-full max-h-[40px] sm:max-h-[81px] h-full absolute -bottom-[0.5px] left-0 z-0"
                        width={320}
                        height={400}
                        src="/assets/images/png/bg_cover.png"
                        alt="bg-cover"
                      />
                    </span>
                    <div className="px-3 relative z-10">
                      <p className="text-sm sm:text-base md:text-lg xl:text-xl font-semibold text-center xl:leading-[30px] ff_poppins mb-1 xl:mb-3 mt-1 xl:mt-[11px] capitalize">
                        {getFormattedCountryName(
                          item.country_name,
                          checkCountry,
                          replacedName,
                        )}
                      </p>
                      <p className="text-xs sm:text-base md:text-lg 3xl:text-xl font-semibold text-center xl:leading-[27px] ff_poppins text-[#660099] flex items-center flex-col">
                        Universities For you{" "}
                        <span className="inline-block">
                          {item.university_count}
                        </span>
                      </p>
                    </div>
                  </Link>
                );
              })}
        </div>
      </div>
    </div>
  );
};

export default AllUniversitiesCards;
